import React, { useState, useEffect, useCallback } from "react";

/**
 * CountdownTimer — muestra el tiempo restante hasta una fecha objetivo.
 * Props:
 *   targetDate (string|Date): fecha/hora de finalización (ej: "2026-05-17T19:00:00+03:00")
 *   onExpired (function): callback opcional cuando el contador llega a cero
 */
export default function CountdownTimer({ targetDate, onExpired }) {
  const [timeLeft, setTimeLeft] = useState(null);

  const calcTimeLeft = useCallback(() => {
    const now = Date.now();
    const target = new Date(targetDate).getTime();
    const diff = target - now;

    if (diff <= 0) {
      return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { expired: false, days, hours, minutes, seconds };
  }, [targetDate]);

  useEffect(() => {
    setTimeLeft(calcTimeLeft());

    const interval = setInterval(() => {
      const result = calcTimeLeft();
      setTimeLeft(result);

      if (result.expired) {
        clearInterval(interval);
        if (onExpired) onExpired();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [calcTimeLeft, onExpired]);

  if (!timeLeft) return null;

  if (timeLeft.expired) {
    return (
      <span className="font-sans text-[#A99F96] font-light text-sm tracking-wider">
        Предложение закончилось
      </span>
    );
  }

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <span className="font-sans text-[#F2E9DE] font-light tracking-[0.15em]">
      {timeLeft.days > 0 && (
        <>
          <span className="text-2xl md:text-3xl font-light">{timeLeft.days}</span>
          <span className="text-xs uppercase tracking-[0.2em] text-[#A99F96] mx-1">
            дней
          </span>
        </>
      )}
      <span className="text-2xl md:text-3xl font-light">{pad(timeLeft.hours)}</span>
      <span className="text-[#E57324] mx-1 text-xl">:</span>
      <span className="text-2xl md:text-3xl font-light">{pad(timeLeft.minutes)}</span>
      <span className="text-[#E57324] mx-1 text-xl">:</span>
      <span className="text-2xl md:text-3xl font-light">{pad(timeLeft.seconds)}</span>
    </span>
  );
}
